import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image, Video, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from 'sonner';

const CloudinaryUpload = ({ 
  onUpload, 
  type = 'image',
  folder = 'products',
  currentUrl = null,
  onRemove = null,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(currentUrl);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const acceptTypes = type === 'image' 
    ? 'image/jpeg,image/png,image/webp,image/gif'
    : 'video/mp4,video/webm,video/quicktime';

  const maxSize = type === 'image' ? 10 * 1024 * 1024 : 100 * 1024 * 1024;

  const resetInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`Arquivo muito grande. Máximo: ${type === 'image' ? '10MB' : '100MB'}`);
      resetInput();
      return;
    }

    // Validate file type
    const validTypes = type === 'image' 
      ? ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
      : ['video/mp4', 'video/webm', 'video/quicktime'];
    
    if (!validTypes.includes(file.type)) {
      toast.error(`Tipo de arquivo não suportado`);
      resetInput();
      return;
    }

    await uploadToCloudinary(file);
  };

  const uploadToCloudinary = async (file) => {
    setUploading(true);
    setProgress(0);
    setUploadComplete(false);

    try {
      // Get signature from backend
      const sigResponse = await api.get(`/cloudinary/signature?resource_type=${type}&folder=${folder}`);
      const { signature, timestamp, cloud_name, api_key, folder: signedFolder } = sigResponse.data;

      if (!cloud_name) {
        throw new Error('Cloudinary não configurado');
      }

      setProgress(20);

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      formData.append('folder', signedFolder);

      // Upload using fetch instead of XHR for better React compatibility
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloud_name}/${type}/upload`;

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Falha no upload');
      }

      const result = await response.json();
      
      setProgress(100);
      setUploadComplete(true);
      
      const secureUrl = result.secure_url;
      
      // Update preview and notify parent
      setPreviewUrl(secureUrl);
      onUpload(secureUrl);
      
      toast.success(`${type === 'image' ? 'Imagem' : 'Vídeo'} enviado com sucesso!`);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || `Erro ao enviar ${type === 'image' ? 'imagem' : 'vídeo'}`);
      setPreviewUrl(currentUrl);
    } finally {
      setUploading(false);
      setProgress(0);
      resetInput();
    }
  };

  const handleRemove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewUrl(null);
    setUploadComplete(false);
    resetInput();
    if (onRemove) {
      onRemove();
    }
  }, [onRemove, resetInput]);

  const triggerFileInput = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [uploading]);

  // Render preview content
  const renderPreview = () => {
    if (!previewUrl) return null;

    if (type === 'image') {
      return (
        <div className="relative w-full h-full group">
          <img 
            src={previewUrl} 
            alt="Preview" 
            className="w-full h-full object-cover rounded-xl"
            onError={() => setPreviewUrl(null)}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={triggerFileInput}
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-1" />
              Trocar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              disabled={uploading}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          {uploadComplete && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      );
    }

    // Video preview
    return (
      <div className="relative w-full h-full">
        <video 
          src={previewUrl} 
          className="w-full h-full object-cover rounded-xl bg-black"
          controls
        />
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="absolute top-2 right-2"
          onClick={handleRemove}
          disabled={uploading}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  // Render upload button
  const renderUploadButton = () => {
    if (uploading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Enviando... {progress}%</span>
          <div className="w-full max-w-[120px] h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-2 p-4">
        {type === 'image' ? (
          <Image className="w-10 h-10 text-slate-400" />
        ) : (
          <Video className="w-10 h-10 text-slate-400" />
        )}
        <div className="text-center">
          <p className="text-sm font-medium text-slate-600">
            Clique para enviar {type === 'image' ? 'imagem' : 'vídeo'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {type === 'image' ? 'JPG, PNG, WebP (máx. 10MB)' : 'MP4, WebM, MOV (máx. 100MB)'}
          </p>
        </div>
      </div>
    );
  };

  const aspectClass = type === 'image' ? 'aspect-square' : 'aspect-video';

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
        data-testid={`${type}-upload-input`}
      />

      <div 
        className={`
          ${aspectClass} w-full rounded-xl overflow-hidden
          ${previewUrl ? 'border-2 border-slate-200' : 'border-2 border-dashed border-slate-300 hover:border-primary hover:bg-primary/5'}
          transition-colors cursor-pointer
          ${uploading ? 'pointer-events-none opacity-70' : ''}
        `}
        onClick={!previewUrl ? triggerFileInput : undefined}
        data-testid={`${type}-upload-container`}
      >
        {previewUrl ? renderPreview() : renderUploadButton()}
      </div>
    </div>
  );
};

export default CloudinaryUpload;
